/**
 * OWNER-only Bloom open sound.
 * Plays /bloom-open.wav once per browser tab/session when an OWNER session enters Bloom.
 * Autoplay failures arm a single gesture retry, then mark played. Never blocks login.
 * Mute follows the existing Universal Tools sound preference (allegiant_pulse_sound_mode).
 */

import { isPulseAudioEnabled } from './pulseAudio';

const OWNER_OPEN_SESSION_KEY = 'bloom_owner_open_played';
const OWNER_OPEN_ASSET = '/bloom-open.wav';

function markPlayed(): void {
  try {
    sessionStorage.setItem(OWNER_OPEN_SESSION_KEY, '1');
  } catch {
    // ignore
  }
}

function alreadyPlayed(): boolean {
  try {
    return sessionStorage.getItem(OWNER_OPEN_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function isOwnerSession(user?: { role?: string; activeRole?: string } | null): boolean {
  if (!user) return false;
  const role = user.activeRole || user.role;
  return role === 'OWNER';
}

function playAssetOnce(audio: HTMLAudioElement): Promise<void> {
  audio.loop = false;
  return audio.play().then(() => undefined);
}

function armFirstInteractionPlay(audio: HTMLAudioElement): void {
  if (typeof window === 'undefined') return;
  const events = ['pointerdown', 'keydown', 'touchstart', 'click'] as const;
  const handler = () => {
    events.forEach((evt) => window.removeEventListener(evt, handler, true));
    if (!isPulseAudioEnabled()) {
      markPlayed();
      return;
    }
    playAssetOnce(audio).catch(() => {}).finally(() => markPlayed());
  };
  events.forEach((evt) => {
    window.addEventListener(evt, handler, { capture: true, once: true, passive: true });
  });
}

let lastNavPlayAt = 0;
const NAV_COOLDOWN_MS = 200;

/** OWNER-only: play the Bloom seal once per intentional primary-nav click. Skips until startup/login sound has resolved so the gesture fallback cannot double-fire. */
export function playOwnerBloomNavSound(user?: { role?: string; activeRole?: string } | null): void {
  if (typeof window === 'undefined') return;
  if (!isOwnerSession(user)) return;
  if (!isPulseAudioEnabled()) return;
  if (!alreadyPlayed()) return;

  const now = Date.now();
  if (now - lastNavPlayAt < NAV_COOLDOWN_MS) return;
  lastNavPlayAt = now;

  try {
    const audio = new Audio(OWNER_OPEN_ASSET);
    audio.preload = 'auto';
    audio.loop = false;
    playAssetOnce(audio).catch(() => {});
  } catch {
    // ignore playback errors
  }
}

export function playOwnerBloomOpenSound(user?: { role?: string; activeRole?: string } | null): void {
  if (typeof window === 'undefined') return;
  if (!isOwnerSession(user)) return;
  if (alreadyPlayed()) return;
  if (!isPulseAudioEnabled()) {
    markPlayed();
    return;
  }

  try {
    const audio = new Audio(OWNER_OPEN_ASSET);
    audio.preload = 'auto';
    audio.loop = false;
    playAssetOnce(audio)
      .then(() => {
        markPlayed();
      })
      .catch(() => {
        armFirstInteractionPlay(audio);
      });
  } catch {
    markPlayed();
  }
}
